import React, { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { useAtomValue, useSetAtom } from "jotai/react";

import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { UpdateHouseholdAddressesDialog } from "@/blocks/UpdateHouseholdAddressesDialog";
import { VnBox } from "@/components/VnBox";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import { useUpdateHouseholdAddressesDialog } from "@/hooks/useUpdateHouseholdAddressesDialog";
import { HouseholdMemberAddressForm } from "@/interactors/HouseholdMemberAddressForm";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";

export const ApplicationAddressScreen = () => {
  const router = useRouter();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);
  const { mutate: updateCurrentPatient } = useUpdateCurrentPatientMutation();

  const { data: householdMembers } = useListHouseholdMembersQuery();

  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const { handlePrimaryApplicantSaveSuccess, dialogProps } =
    useUpdateHouseholdAddressesDialog();

  const handleToggleExpand = (memberId: string) => {
    setExpandedMemberId(prev => (prev === memberId ? null : memberId));
  };

  const handleClose = () => {
    setExpandedMemberId(null);
  };

  const isNextDisabled = useMemo(() => {
    if (!householdMembers) return true;
    return householdMembers.some(member => {
      const shouldNeedMainAddress =
        !member.isHomeless && !member.mainAddressStreetOne;
      const shouldNeedMailingAddress =
        !member.hasNoMailingAddress && !member.mailingAddressStreetOne;
      return shouldNeedMainAddress || shouldNeedMailingAddress;
    });
  }, [householdMembers]);

  const handleNext = () => {
    if (isNextDisabled) {
      return;
    }

    updateCurrentPatient(
      {
        patientData: {
          applicationLastStep: "ADDRESS",
        },
      },
      {
        onSuccess: () => {
          setApplicationCurrentStep(step =>
            Math.min(step + 1, applicationTotalSteps),
          );

          router.navigate({
            pathname: "/(authed)/(modal)/application/marital-status",
          });
        },
      },
    );
  };

  return (
    <>
      <ApplicationScreenLayout
        onNextButtonPress={handleNext}
        isNextButtonDisabled={isNextDisabled}
        titleI18nKey="native:applicationAddressScreen.title"
        descriptionI18nKey="native:applicationAddressScreen.description">
        <VnBox className="gap-4">
          {householdMembers?.map(member => {
            const isExpanded = expandedMemberId === member.id;

            return (
              <HouseholdMemberAddressForm
                key={member.id}
                member={member}
                isExpanded={isExpanded}
                onToggleExpand={() => handleToggleExpand(member.id)}
                onClose={handleClose}
                onPrimaryApplicantSaveSuccess={
                  handlePrimaryApplicantSaveSuccess
                }
              />
            );
          })}
        </VnBox>
      </ApplicationScreenLayout>

      <UpdateHouseholdAddressesDialog {...dialogProps} />
    </>
  );
};
